import { h, Component, ComponentType } from "preact";

import { rectContainsPoint } from "../../foundation/utils/rectangle";

import styles from "./withHoverRegion.module.css";

interface HoverRegionProps {
    initialHover?: boolean;
    ignoreOverlays?: boolean;
    onHoverChange?: (hover: boolean) => void;
}

interface State {
    hover: boolean;
}

/**
 * A HOC that makes hover state accesible in JS context so that it can be passed down in the hierarchy when needed.
 * `WrappedComponent` receives the hover state with a prop called `hover`.
 *
 * @param {preact.Component} WrappedComponent
 */
function withHoverRegion<P extends HoverRegionProps>(WrappedComponent: ComponentType<P>) {
    return class extends Component<Omit<P, "hover">, State> {
        override state = {
            hover: false
        };

        changeHover = (hover: boolean) => {
            this.setState({ hover });

            this.props.onHoverChange?.(hover);
        };

        handleMouseEnter = () => {
            this.changeHover(true);
        };

        handleMouseLeave = (event: MouseEvent) => {
            const { initialHover, ignoreOverlays = true } = this.props;
            const { clientX: x, clientY: y, relatedTarget } = event;

            const targetNode = event.target as HTMLElement;
            const rect = targetNode.getBoundingClientRect();

            if (initialHover) {
                return;
            } else if (ignoreOverlays && relatedTarget && rectContainsPoint(rect, { x, y })) {
                const hoverOff = () => this.changeHover(false);

                relatedTarget.addEventListener("mouseleave", function relatedHandler(e: MouseEvent) {
                    if (!rectContainsPoint(rect, { x: e.clientX, y: e.clientY })) {
                        hoverOff();
                    }

                    relatedTarget.removeEventListener("mouseleave", relatedHandler as EventListener);
                } as EventListener);
                return;
            }

            this.changeHover(false);
        };

        override componentWillReceiveProps({ initialHover }: { initialHover?: boolean; }) {
            if (typeof initialHover !== "undefined" && initialHover !== this.state.hover) {
                this.setState({ hover: initialHover });
            }
        }

        render() {
            return (
                <div
                    class={styles.hoverRegion}
                    onMouseEnter={this.handleMouseEnter}
                    onMouseLeave={this.handleMouseLeave}>
                    <WrappedComponent
                        {...this.props as P}
                        hover={this.state.hover}/>
                </div>
            );
        }
    };
}

withHoverRegion.displayName = "HoverRegion";

withHoverRegion.defaultProps = {
    initialHover: false,
    ignoreOverlays: true
};

export { HoverRegionProps };
export default withHoverRegion;
